/* eslint-disable react/no-this-in-sfc */
import classNames from "components/ui/classNames";
import formatDistanceStrict from "date-fns/formatDistanceStrict";
import enUS from "date-fns/locale/en-US";
import ptBR from "date-fns/locale/pt-BR";
import parseISO from "date-fns/parseISO";
import type { IQuotesHistoryQuery } from "lib/graphql/queries/__generated__/QuotesHistoryQuery.generated";
import { useIntl } from "react-intl";

interface ReturnBoxProps {
  title: string;
  tickerSymbol: string;
  firstPoint: IQuotesHistoryQuery["quotesHistory"][number]["nodes"][number];
  lastPoint: IQuotesHistoryQuery["quotesHistory"][number]["nodes"][number];
  returnRate: number;
  textColor: string;
  className?: string;
  tickerSymbolClassName?: string;
  style?: React.CSSProperties;
}
export default function ReturnBox({
  title,
  firstPoint,
  lastPoint,
  returnRate,
  tickerSymbol,
  textColor,
  className,
  tickerSymbolClassName,
  style,
}: ReturnBoxProps) {
  const intl = useIntl();

  const isNegativeReturn = returnRate < 0;
  const isPositiveReturn = returnRate > 0;
  const isNeutralReturn = returnRate >= 0 && returnRate < 0.1;

  const percentageFormatter = new Intl.NumberFormat("en-US", {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const valueFormatter = new Intl.NumberFormat(intl.locale, {
    notation: "compact",
    compactDisplay: "short",
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });

  const isBigValue = returnRate >= 10000;

  return (
    <div
      key={tickerSymbol}
      style={style}
      className={classNames(
        "group relative inline-flex w-full flex-col rounded-xl p-2.5 sm:max-w-36",
        "bg-slate-50/80",
        className,
        {
          "text-rose-600 dark:text-rose-400": isNegativeReturn,
          "text-emerald-600 dark:text-emerald-400": isPositiveReturn,
          "text-slate-600 dark:text-slate-400": isNeutralReturn,
        },
      )}
    >
      {textColor && (
        <span aria-hidden={true} className="absolute right-2 top-0">
          <span
            className="inline-flex h-2 w-2 items-center justify-center rounded-full bg-violet-700 opacity-40"
            style={{ backgroundColor: textColor || undefined }}
          />
        </span>
      )}

      <strong className="font-mono text-2xl font-bold">
        {isBigValue ? `${valueFormatter.format(returnRate)} %` : percentageFormatter.format(returnRate)}
      </strong>

      <div className="mt-1.5 space-y-1">
        <p className="font-mono text-xs font-normal text-slate-600 dark:text-slate-400">
          {formatDistanceStrict(parseISO(firstPoint.date), parseISO(lastPoint.date), {
            locale: intl.locale === "pt-BR" ? ptBR : enUS,
          })}
        </p>
        <h4 className="text-xs font-bold text-slate-800 dark:text-slate-200">{title}</h4>
      </div>

      <span
        className={classNames(
          "mt-2 rounded-md bg-zinc-100 p-1 text-center font-mono text-xs font-bold tracking-wider text-slate-700 dark:bg-zinc-900 dark:text-slate-300",
          tickerSymbolClassName,
        )}
      >
        {tickerSymbol}
      </span>
    </div>
  );
}
